import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Button, Dialog } from '@material-ui/core';
import EditTwoToneIcon from '@material-ui/icons/EditTwoTone';
import TagSelect, { tagsToOptions } from 'components/Elements/TagSelect';
import { PergoTextField } from 'components/PergoTextField/PergoTextField';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { MapContext } from 'context/mapContext/mapContextContainer';
import { CallContext } from 'context/callContextContainer';
import Config from 'config/index';
import { clearCurrency, formatPhone } from 'utils/text';
import { fareService } from 'services/fareService';
import { accountsService } from 'services/accountsService';
import { vehiclesService } from 'services/vehiclesService';
import { jobsService } from 'services/jobsService';
import JobSectionContent from './JobSectionContent';
import EditSmsPhoneModal from './EditSmsPhoneModal';
import { DialogTitleContainer, DialogContentContainer, DialogActionsContainer } from './style';

const Step2 = ({ globalValues, onClickNext, onClickPrev, onClickMiddle, showPayNow, isCurrentStep, isCallCenter }) => {
  const [values, setValues] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [reservationError, setReservationError] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { mapData } = useContext(MapContext);
  const { siteSetting, callcenterActiveDb } = useContext(CallContext);
  const [editSmsPhoneOpen, setEditSmsPhoneOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    async function initializeStep() {
      setValues({
        ...values,
        distance: globalValues.distance,
        duration: globalValues.duration || mapData.duration || 0,
        deadhead_distance: globalValues.deadhead_distance || mapData.deadhead_distance || 0,
        passenger_count: globalValues.passenger_count,
        passenger_id: globalValues.passenger_id,
        passenger_phone: globalValues.phone,
        passenger_sms_phone: globalValues.sms_phone,
        bags: globalValues.bags,
        vehicle_type: globalValues.vehicle_type,
        fare: globalValues.fare,
        tip: globalValues.tip,
        tolls: globalValues.tolls,
        tip_percent: globalValues.tip_percent,
        instruction: globalValues.instruction,
        send_text_update: globalValues.send_text_update,
        tax_included: globalValues.tax_included,
        account_id: globalValues.account_id != 0 ? globalValues.account_id : -1,
        prior_auth_number: globalValues.prior_auth_number,
        fare_calc_type: globalValues.fare_calc_type,
        fare_duration: globalValues.fare_duration,
        booking_deposit_percent: globalValues.booking_deposit_percent || 0,
        step_changed: false,
        tags_require: tagsToOptions(globalValues.required_tags || globalValues.tags || []),
        tags_exclude: tagsToOptions(globalValues.excluded_tags || []),
        pickup_zip: globalValues.pickup_zip,
        is_handicapped: globalValues.is_handicapped == 1 ? true : false,
        is_fare_edited: globalValues.is_fare_edited == 1 ? true : false,
        is_mas: globalValues.is_mas,
      });

      showCircularProgress('Loading');
      const data = await accountsService.get_accounts_list({
        ...(isCallCenter && { dbname: callcenterActiveDb }),
      });
      if (data?.data && data.data.status === 'SUCCESS') {
        setAccounts(data.data.data);
      }
      const data1 = await vehiclesService.get_vehicle_types();
      if (data1?.data && data1.data.status === 'SUCCESS') {
        setVehicleTypes(data1.data.data);
      }
      const data2 = await jobsService.get_tags();
      if (data2?.data && data2.data.status === 'SUCCESS') {
        setAllTags(data2.data.data);
      }
      hideCircularProgress();

      setReservationError(false);
    }

    if (isCurrentStep) {
      initializeStep();
    }
  }, [isCurrentStep]);

  useEffect(() => {
    showCircularProgress('Calculating');
    fareService.get_fare_params({
      account_id: values.account_id,
      pickup_zip: values.pickup_zip,
    }).then(({ data }) => {
      if (data.status === 'SUCCESS') {
        setValues({
          ...values,
          tax_rate: data.data.tax || 0,
        });
      }
    }).finally(() => hideCircularProgress());
  }, [values.account_id]);

  useEffect(() => {
    if (values.tax_included == 1 && values.fare > 0) {
      setValues({
        ...values,
        tax_amount: (values.fare * (values.tax_rate / 100)) / 100,
      });
    }
  }, [values.tax_rate, values.fare]);

  const calcFare = (calcValues) => {
    if (values.is_mas || calcValues.is_fare_edited) {
      return;
    }

    if (calcValues?.fare_calc_type == 1 || parseFloat(calcValues.distance) > 0) {
      showCircularProgress('Calculating');
      fareService.calc_fare({
        distance: calcValues.distance,
        deadhead_distance: calcValues.deadhead_distance,
        passenger_count: calcValues.passenger_count,
        vehicle_type: calcValues.vehicle_type,
        baggage_count: calcValues.bags,
        fare_calc_type: calcValues.fare_calc_type,
        fare_duration: calcValues.fare_duration,
        tax_included: calcValues.tax_included,
        pickup_zip: calcValues.pickup_zip,
        is_handicapped: calcValues.is_handicapped,
        passenger_phone: calcValues.passenger_phone,
        trip_duration: 0,
        ...(calcValues.pickup_zone && { pickup_zone: calcValues.pickup_zone }),
        ...(calcValues.dropoff_zone && { dropoff_zone: calcValues.dropoff_zone }),
        ...(isCallCenter && { dbname: callcenterActiveDb }),
        account_id: calcValues.account_id,
      }).then(({ data }) => {
        if (data.status === 'SUCCESS') {
          if (data.data.num_rows >= 1) {
            setValues({
              ...calcValues,
              fare: data.data.data.fare * 100,
              tip: data.data.data.tip * 100,
            });
          }
        }
      }).finally(() => hideCircularProgress());
    }
  };

  const onHandleChange = (id, newValue) => {
    const tmpValues = { ...values };
    tmpValues[id] = newValue;
    if (id === 'fare' || id === 'tip' || id === 'tolls') {
      tmpValues[id] = parseFloat(newValue.slice(2).replace(/\D/g, ''));
      if (id === 'fare') {
        tmpValues.is_fare_edited = true;
      }
    } else if (id === 'tip_percent') {
      tmpValues.tip = (tmpValues.fare * tmpValues.tip_percent) / 100;
    } else if (id === 'account_id') {
      tmpValues.prior_auth_number = '';
    }
    tmpValues['step_changed'] = true;
    setValues({
      ...tmpValues,
    });

    if (id === 'passenger_count' || id === 'bags' || id === 'vehicle_type' || id === 'fare_calc_type' || id === 'fare_duration' || id === 'account_id' || id === 'is_handicapped') {
      calcFare(tmpValues);
    }
  };

  const onNextReservationStep = () => {
    // A dispatched ASAP job cannot be turned into a reservation — except an
    // Expired (12) one: it is no longer live, and re-dating it as a reservation
    // is the only restore path.
    if (globalValues.job_type == 1 && globalValues.status >= 1 && parseInt(globalValues.status, 10) !== 12) {
      setReservationError(true);
      return;
    }

    setReservationError(false);
    onClickNext(values);
  };

  const onNextDispatchStep = () => {
    onClickMiddle(values);
  };

  const handleTagChange = (id, newValue) => {
    setValues({
      ...values,
      [id]: newValue,
      step_changed: true,
    });
  };

  const handleUpdateSmsPhone = () => {
    setEditSmsPhoneOpen(true);
  };

  const site_setting = siteSetting();
  const fare_params = site_setting && JSON.parse(site_setting.fare_params);
  const totalFare = values.fare + values.tip + values.tolls;
  const paidAmount = globalValues?.payments?.reduce((acc, cur) => acc + cur.amount * 100, 0) || 0;
  const bookingDeposit = parseFloat(values.booking_deposit_percent || 0) * totalFare;
  const bdBalance = parseFloat(parseFloat(bookingDeposit / 100).toFixed(2)) - parseFloat(parseFloat(paidAmount / 100).toFixed(2));
  let selAccount = accounts?.length > 0 && parseInt(values.account_id) > 0 ? accounts.filter(acc => acc.id == values.account_id) : false;
  selAccount = selAccount?.[0] ? JSON.parse(selAccount[0].kinetik_account_data) : false;

  const reqTags = values.tags_require || [];
  const excTags = values.tags_exclude || [];
  const hasTags = reqTags.length > 0 || excTags.length > 0;

  return (
    <StepContent
      title="Job Detail"
      prevButton={{
        onClick: onClickPrev,
        label: 'Back',
      }}
      middleButton={{
        onClick: onNextDispatchStep,
        label: 'Dispatch',
      }}
      nextButton={{
        onClick: onNextReservationStep,
        label: 'Reservation',
      }}
    >
      <div className="form-control-inline">
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Accounts</InputLabel>
          <Select
            value={Number.isNaN(parseInt(values.account_id)) ? -1 : parseInt(values.account_id)}
            onChange={e => onHandleChange('account_id', e.target.value)}
          >
            <MenuItem value={-1}>No Account</MenuItem>
            {accounts?.map((item, index) => <MenuItem value={parseInt(item.id)} key={index}>{item.account_name}</MenuItem>)}
          </Select>
        </FormControl>
        {selAccount ? (
          <PergoTextField
            label="Account Type"
            value={selAccount.type}
            InputProps={{ disabled: true }}
          />
        ) : (
          <PergoTextField
            id="prior_auth_number"
            label="Prior Auth Number"
            values={values}
            handleChange={onHandleChange}
            InputProps={{ disabled: !values?.account_id || values?.account_id == -1 || values.is_mas }}
          />
        )}
      </div>
      <div className="form-control-inline">
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Passengers</InputLabel>
          <Select
            value={parseInt(values.passenger_count) || 1}
            onChange={e => onHandleChange('passenger_count', e.target.value)}
          >
            {[...Array(20).keys()].map((item, index) => (
              <MenuItem value={index + 1} key={index}>{`${index + 1} passenger${index > 0 ? 's' : ''
                }`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Bags</InputLabel>
          <Select value={parseInt(values.bags || 0)} onChange={e => onHandleChange('bags', e.target.value)}>
            <MenuItem value={0}>No Luggage</MenuItem>
            {[...Array(6).keys()].map((item, index) => (
              <MenuItem value={index + 1} key={index}>{`${index + 1} bag${index > 0 ? 's' : ''
                }`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Vehicle Type</InputLabel>
          <Select
            value={parseInt(values.vehicle_type) || -1}
            onChange={e => onHandleChange('vehicle_type', e.target.value)}
          >
            <MenuItem value={-1}>Any</MenuItem>
            {vehicleTypes.map((item, index) => <MenuItem value={item} key={index}>{Config.VEHICLE_TYPE_LIST[item]}</MenuItem>)}
          </Select>
        </FormControl>
      </div>

      <div className="form-control-inline">
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Fare Calc Type</InputLabel>
          <Select
            value={parseInt(values.fare_calc_type) || 0}
            onChange={e => onHandleChange('fare_calc_type', e.target.value)}
          >
            <MenuItem value={0}>Point To Point</MenuItem>
            <MenuItem value={1}>By Time</MenuItem>
          </Select>
        </FormControl>
        {values.fare_calc_type == 0 ? (
          <PergoTextField
            label="Distance"
            value={`${values?.distance ? `${values.distance} miles` : ''}${Number.isNaN(parseInt(values.duration)) ? '' : ` (Est: ${parseInt(values.duration)} mins)`}`}
            InputProps={{ readOnly: true }}
          />
        ) : (
          <FormControl variant="outlined">
            <InputLabel htmlFor="outlined-age-native-simple">Fare Duration</InputLabel>
            <Select
              value={parseInt(values.fare_duration) || 30}
              onChange={e => onHandleChange('fare_duration', e.target.value)}
            >
              {[...Array(20).keys()].map((item, index) => (
                <MenuItem value={(index + 1) * 30} key={index}>{`${(index + 1) * 30} minutes`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      <div className="form-control-inline mb-0">
        <PergoTextField
          id="fare"
          label="Fare $"
          type="currency"
          values={values}
          handleChange={onHandleChange}
        />
        <PergoTextField
          id="tolls"
          label="Tolls $"
          type="currency"
          values={values}
          handleChange={onHandleChange}
        />
        <PergoTextField
          id="tip"
          label="Tip $"
          type="currency"
          values={values}
          handleChange={onHandleChange}
        />
        <FormControl variant="outlined" style={{ flex: 0.5 }}>
          <InputLabel htmlFor="outlined-age-native-simple">Tip %</InputLabel>
          <Select
            value={values.tip_percent || 0}
            onChange={e => onHandleChange('tip_percent', e.target.value)}
          >
            <MenuItem value={0}>0%</MenuItem>
            <MenuItem value={5}>5%</MenuItem>
            <MenuItem value={10}>10%</MenuItem>
            <MenuItem value={15}>15%</MenuItem>
            <MenuItem value={20}>20%</MenuItem>
            <MenuItem value={25}>25%</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="form-control-inline">
        <FormControlLabel
          control={
            <Checkbox
              checked={values.tax_included == 1}
              onChange={e => onHandleChange('tax_included', e.target.checked ? 1 : 0)}
            />
          }
          label={`Charge Tax${values.tax_included == 1 && values.tax_amount ? ' (' + clearCurrency(values.tax_amount) + ')' : ''}`}
        />
      </div>
      <div className="tag-field-row">
        <fieldset className="tag-fieldset">
          <legend className="tag-legend">Tags</legend>
          <div className="tag-fieldset-content">
            {hasTags ? (
              <div className="tag-chips">
                {reqTags.map((t, i) => (
                  <span className="tag-chip tag-chip--require" title="Required" key={`r${i}`}>
                    {t.label}
                  </span>
                ))}
                {excTags.map((t, i) => (
                  <span className="tag-chip tag-chip--exclude" title="Excluded" key={`e${i}`}>
                    {t.label}
                  </span>
                ))}
              </div>
            ) : (
              <span className="tag-empty">No tags</span>
            )}
            <Button
              size="small"
              className="tag-edit-btn btn-transition-none"
              onClick={() => setTagModalOpen(true)}
            >
              <EditTwoToneIcon fontSize="small" style={{ marginRight: 4 }} />
              Edit
            </Button>
          </div>
        </fieldset>
      </div>
      <div className="form-control-inline form-control-section mb-0">
        <FormControl variant="outlined">
          <InputLabel htmlFor="outlined-age-native-simple">Booking Deposit</InputLabel>
          <Select
            value={parseFloat(values.booking_deposit_percent || 0)}
            onChange={e => onHandleChange('booking_deposit_percent', e.target.value)}
          >
            {[...Array(21).keys()].map((item, index) => (<MenuItem value={item / 20} key={index}>{item * 5}% - {clearCurrency(totalFare / 20 * item, true)}</MenuItem>))}
          </Select>
        </FormControl>
        <PergoTextField
          label="BD Paid"
          value={clearCurrency(paidAmount, true)}
          readonly
        />
        <PergoTextField
          label="BD Balance"
          value={clearCurrency(bdBalance)}
          readonly
        />
      </div>

      <div className="">
        <PergoTextField
          id="instruction"
          label="Driver Instructions"
          values={values}
          multiline
          rows="1"
          handleChange={onHandleChange}
        />
      </div>

      <div className="form-control-section mt-0 d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(values.send_text_update)}
                onChange={e => onHandleChange('send_text_update', e.target.checked)}
              />
            }
            label={<span>Send Text to</span>}
            style={{ marginRight: '5px' }}
          />
          <b style={{ color: 'var(--blue-main-color)', cursor: 'pointer' }} onClick={handleUpdateSmsPhone}>{formatPhone(values.passenger_sms_phone || values.passenger_phone)}</b>
        </div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(values.is_handicapped)}
              onChange={e => onHandleChange('is_handicapped', e.target.checked)}
            />
          }
          label="Handicapped"
          style={{ marginRight: '0' }}
        />
      </div>
      {reservationError && (
        <div className="form-control-section mt-0">
          <label style={{ lineHeight: '24px', color: 'red' }}>This job is already dispatched</label>
        </div>
      )}

      <EditSmsPhoneModal
        passengerDetail={values}
        updatePassengerDetail={vals => {
          setValues({
            ...values,
            passenger_sms_phone: vals.sms_phone,
          });
        }}
        editOpen={editSmsPhoneOpen}
        handleEditClose={() => setEditSmsPhoneOpen(false)}
      />

      <Dialog
        classes={{ paper: 'modal-content' }}
        fullWidth
        maxWidth="sm"
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
      >
        <DialogTitleContainer className="bg-secondary">
          <span>Edit Tags</span>
        </DialogTitleContainer>
        <DialogContentContainer className="p-0" dividers>
          <div className="dialog-content--wrapper bg-secondary border-0">
            <div className="card-body px-lg-4 py-lg-3">
              <TagSelect
                label="Required Tags (vehicle/driver must have)"
                options={allTags}
                value={values.tags_require}
                excludeValues={values.tags_exclude}
                placeholder="Add required tags"
                onChange={val => handleTagChange('tags_require', val)}
              />
              <TagSelect
                label="Excluded Tags (vehicle/driver must NOT have)"
                options={allTags}
                value={values.tags_exclude}
                excludeValues={values.tags_require}
                placeholder="Add excluded tags"
                onChange={val => handleTagChange('tags_exclude', val)}
              />
            </div>
          </div>
        </DialogContentContainer>
        <DialogActionsContainer className="bg-secondary p-3">
          <Button
            size="small"
            className="btn-primary px-4 py-2 btn-transition-none"
            onClick={() => setTagModalOpen(false)}
          >
            <span className="btn-wrapper--label">Done</span>
          </Button>
        </DialogActionsContainer>
      </Dialog>
    </StepContent>
  );
};

export default Step2;

const StepContent = styled(JobSectionContent)`
  /* Match the MUI outlined inputs: notched label on the top border, same
     border color/radius and ~14px horizontal padding. */
  .tag-field-row {
    margin: 12px 0;
  }

  .tag-fieldset {
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 0;
    padding: 2px 4px 6px 8px;
    margin: 0;
    min-width: 0;
  }

  .tag-fieldset:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }

  .tag-legend {
    width: auto;
    padding: 0 5px;
    margin-bottom: 0;
    font-size: 12px;
    line-height: 1;
    color: var(--gray-main-color, #6b6b6b);
  }

  .tag-fieldset-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 32px;
  }

  .tag-empty {
    font-size: 14px;
    font-weight: 600;
    color: #3b3e66;
  }

  .tag-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  .tag-chip {
    font-size: 12px;
    line-height: 1.4;
    padding: 2px 8px;
    border-radius: 10px;
    cursor: default;
    white-space: nowrap;
  }

  .tag-chip--require {
    background: #e3f6ea;
    color: #1b7f3b;
  }

  .tag-chip--exclude {
    background: #fde4e4;
    color: #c0392b;
  }

  .tag-edit-btn {
    color: var(--blue-main-color);
    font-weight: 600;
    text-transform: none;
    padding: 4px 10px;
    align-self: center;
    flex-shrink: 0;
  }
`;
